import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, CircleDashed, Clock, Tag } from 'lucide-react';
import useCookbookSnippetSkills from '../../hooks/useCookbookSnippetSkills';
import { useDirectPremiumCheck } from '../../hooks/useDirectPremiumCheck';
import IconRenderer from '../ui/IconRenderer';
import MasteryBrainIcon from './MasteryBrainIcon';
import { ReactComponent as UserAchievementStatsPanelDeckIcon } from '../../Icons/deck-icon.svg';
import { ReactComponent as RoadmapNodeIcon } from '../../Icons/roadmap-node-icon.svg';
import { ReactComponent as UserAchievementStatsPanelSearchIcon } from '../../Icons/search.svg';
import { formatRoadmapTrackTitle } from '../../utils/roadmapDisplay';
import { userCanAccessRoadmapTrack } from '../../utils/roadmapSubscription';
import { cookbookSnippetSrsStatusPillVariant } from '../../utils/studySessionDotState';
import './UserAchievementStatsPanel.css';

const USER_PROFILE_SKILLS_PAGE_SIZE_OPTIONS = [10, 20, 50];
const USER_PROFILE_SKILLS_PAGE_SIZE_STORAGE_KEY = 'flashcode-profile-skills-page-size';

function readStoredSkillsPageSize() {
  if (typeof window === 'undefined') return 10;
  try {
    const raw = sessionStorage.getItem(USER_PROFILE_SKILLS_PAGE_SIZE_STORAGE_KEY);
    const n = parseInt(raw, 10);
    if (USER_PROFILE_SKILLS_PAGE_SIZE_OPTIONS.includes(n)) return n;
  } catch {
    /* ignore */
  }
  return 10;
}

function languageIconNameFromKey(langKey) {
  if (!langKey) return 'javascript';
  const k = String(langKey).trim().toLowerCase();
  if (k === 'c++' || k === 'cpp' || k === 'cplusplus') return 'cpp';
  if (k === 'c#' || k === 'csharp') return 'csharp';
  return k.replace(/\s+/g, '') || 'javascript';
}

function languageDisplayLabel(key) {
  if (!key || key === 'other') return 'Other';
  const map = {
    javascript: 'JavaScript',
    typescript: 'TypeScript',
    python: 'Python',
    java: 'Java',
    cpp: 'C++',
    csharp: 'C#'
  };
  const k = String(key).trim().toLowerCase();
  return map[k] || key.charAt(0).toUpperCase() + key.slice(1);
}

function profileSkillsSnippetIsAccessible(snippet, isPremiumUser) {
  if (!snippet.trackId) return true;
  return userCanAccessRoadmapTrack(isPremiumUser, snippet.trackId, {
    trackId: snippet.trackId,
    title: snippet.trackTitle || snippet.trackId
  });
}

/** Human-readable track label for skills filters (catalog titles often mirror track ids). */
function profileSkillsTrackDisplayTitle(rawTitle, snippetLanguage, languageFilter) {
  const raw = (rawTitle && String(rawTitle).trim()) || '';
  if (!raw) return '';
  const snippetLang = snippetLanguage ? String(snippetLanguage).trim() : '';
  const ctx = languageFilter ? String(languageFilter).trim() : snippetLang;
  return formatRoadmapTrackTitle(ctx, raw, { language: snippetLang || ctx });
}


const emptySkillsData = {
  totalSnippets: 0,
  avgMastery: 0,
  avgSnippetLevel: 0,
  snippetsMastered: 0,
  snippets: []
};

const USER_PROFILE_SKILLS_NO_TRACK_FILTER_VALUE = '__no_track__';

const USER_PROFILE_SKILLS_SKELETON_ROW_COUNT = 6;

function UserProfileSkillsSnippetSkeleton({ rows = USER_PROFILE_SKILLS_SKELETON_ROW_COUNT }) {
  return (
    <ul
      className="user-profile-achievement-stats-panel__snippet-list user-profile-achievement-stats-panel__snippet-list--skeleton"
      aria-hidden="true"
    >
      {Array.from({ length: rows }, (_, index) => (
        <li
          key={`skills-skeleton-${index}`}
          className="user-profile-achievement-stats-panel__snippet-row-item"
        >
          <div className="user-profile-achievement-stats-panel__snippet-row user-profile-achievement-stats-panel__snippet-row--skeleton">
            <div className="user-profile-achievement-stats-panel__snippet-body">
              <div className="user-profile-achievement-stats-panel__snippet-skeleton-title-row">
                <span className="user-profile-achievement-stats-panel__snippet-skeleton-icon" />
                <span className="user-profile-achievement-stats-panel__snippet-skeleton-line user-profile-achievement-stats-panel__snippet-skeleton-line--title" />
              </div>
              <div className="user-profile-achievement-stats-panel__snippet-deck-row">
                <span className="user-profile-achievement-stats-panel__snippet-skeleton-line user-profile-achievement-stats-panel__snippet-skeleton-line--deck" />
                <div className="user-profile-achievement-stats-panel__snippet-skeleton-metrics">
                  <span className="user-profile-achievement-stats-panel__snippet-skeleton-pill" />
                  <span className="user-profile-achievement-stats-panel__snippet-skeleton-pill user-profile-achievement-stats-panel__snippet-skeleton-pill--short" />
                </div>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function UserProfileSkillsFiltersSkeleton() {
  return (
    <div
      className="user-profile-achievement-stats-panel__skills-filters user-profile-achievement-stats-panel__skills-filters--skeleton"
      aria-hidden="true"
    >
      <div className="user-profile-achievement-stats-panel__skills-filters-main">
        <span className="user-profile-achievement-stats-panel__skills-filter-skeleton" />
        <span className="user-profile-achievement-stats-panel__skills-filter-skeleton" />
        <span className="user-profile-achievement-stats-panel__skills-filter-skeleton user-profile-achievement-stats-panel__skills-filter-skeleton--wide" />
      </div>
      <span className="user-profile-achievement-stats-panel__skills-search-skeleton" />
    </div>
  );
}

function UserProfileSkillsStatusToggle({
  checked,
  onChange,
  showLabel,
  hideLabel,
  ariaLabelOn,
  ariaLabelOff,
  separated = false,
  icon = null,
  variant = 'default'
}) {
  return (
    <label
      className={`user-profile-achievement-stats-panel__skills-mastered-checkbox${
        separated ? ' user-profile-achievement-stats-panel__skills-mastered-checkbox--separated' : ''
      }`}
      title={checked ? hideLabel : showLabel}
    >
      <input
        type="checkbox"
        className="user-profile-achievement-stats-panel__skills-mastered-checkbox__input"
        checked={checked}
        onChange={onChange}
        aria-label={checked ? ariaLabelOn : ariaLabelOff}
      />
      <span
        className={[
          'user-profile-achievement-stats-panel__skills-mastered-checkbox__icon-wrap',
          checked
            ? `user-profile-achievement-stats-panel__skills-mastered-checkbox__icon-wrap--checked user-profile-achievement-stats-panel__skills-mastered-checkbox__icon-wrap--${variant}`
            : ''
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden
      >
        {icon ? (
          <span className="user-profile-achievement-stats-panel__skills-mastered-checkbox__icon">
            {icon}
          </span>
        ) : null}
        <span className="user-profile-achievement-stats-panel__skills-mastered-checkbox__label">
          {checked ? hideLabel : showLabel}
        </span>
      </span>
    </label>
  );
}

function profileSkillsSnippetMatchesStatusFilters(snippet, filters) {
  const { showMastered, showDue, showUnstarted } = filters;
  if (!showMastered && !showDue && !showUnstarted) return true;
  return (
    (showMastered && snippet.is_mastered) ||
    (showDue && snippet.is_due_today) ||
    (showUnstarted && !snippet.is_started)
  );
}

/**
 * Profile Achievements → Snippets: paginated cookbook cards with SRS review status.
 */
export default function UserAchievementStatsPanel({
  userId,
  languageFilter,
  canView,
  onStartStudying,
  skillsTabRefocusNonce = 0
}) {
  const {
    data: skillsData = emptySkillsData,
    isFetching
  } = useCookbookSnippetSkills(userId, languageFilter, { enabled: canView });
  const { isPremiumUser } = useDirectPremiumCheck(userId);

  const [pageSize, setPageSize] = useState(readStoredSkillsPageSize);
  const [currentPage, setCurrentPage] = useState(1);
  const [skillsTrackFilter, setSkillsTrackFilter] = useState('');
  const [skillsTagFilter, setSkillsTagFilter] = useState('');
  const [skillsSnippetSearch, setSkillsSnippetSearch] = useState('');
  const [skillsSearchOpen, setSkillsSearchOpen] = useState(false);
  const [skillsShowMasteredOnly, setSkillsShowMasteredOnly] = useState(false);
  const [skillsShowDueOnly, setSkillsShowDueOnly] = useState(false);
  const [skillsShowUnstartedOnly, setSkillsShowUnstartedOnly] = useState(false);
  const skillsTrackFilterWrapRef = useRef(null);
  const skillsTagFilterWrapRef = useRef(null);
  const skillsSearchInputRef = useRef(null);
  const [skillsFilterMenuOpen, setSkillsFilterMenuOpen] = useState(null);

  // placeholderData supplies empty rows immediately, so isLoading stays false while the first fetch runs
  const showInitialSkeleton = isFetching && skillsData.snippets.length === 0;
  const isRefreshingSkills = isFetching && skillsData.snippets.length > 0;

  useEffect(() => {
    setSkillsTrackFilter('');
    setSkillsTagFilter('');
    setSkillsSnippetSearch('');
    setSkillsSearchOpen(false);
    setSkillsShowMasteredOnly(false);
    setSkillsShowDueOnly(false);
    setSkillsShowUnstartedOnly(false);
    setSkillsFilterMenuOpen(null);
  }, [languageFilter, userId]);

  useEffect(() => {
    if (!skillsTabRefocusNonce) return;
    setSkillsTrackFilter('');
    setSkillsTagFilter('');
    setSkillsSnippetSearch('');
    setSkillsSearchOpen(false);
    setSkillsShowMasteredOnly(false);
    setSkillsShowDueOnly(false);
    setSkillsShowUnstartedOnly(false);
    setSkillsFilterMenuOpen(null);
  }, [skillsTabRefocusNonce]);

  useEffect(() => {
    if (!skillsFilterMenuOpen) return undefined;
    const onDocMouseDown = (e) => {
      const t = skillsTrackFilterWrapRef.current;
      const g = skillsTagFilterWrapRef.current;
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (skillsFilterMenuOpen === 'track' && t && !t.contains(target)) {
        setSkillsFilterMenuOpen(null);
      }
      if (skillsFilterMenuOpen === 'tag' && g && !g.contains(target)) {
        setSkillsFilterMenuOpen(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setSkillsFilterMenuOpen(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [skillsFilterMenuOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    languageFilter,
    userId,
    skillsTrackFilter,
    skillsTagFilter,
    skillsSnippetSearch,
    skillsShowMasteredOnly,
    skillsShowDueOnly,
    skillsShowUnstartedOnly
  ]);

  const snippetsSource = skillsData.snippets;

  const accessibleSnippets = useMemo(() => {
    if (isPremiumUser) return snippetsSource;
    return snippetsSource.filter((s) => profileSkillsSnippetIsAccessible(s, false));
  }, [snippetsSource, isPremiumUser]);

  const trackFilterOptions = useMemo(() => {
    const map = new Map();
    for (const s of accessibleSnippets) {
      if (s.trackId && !map.has(s.trackId)) {
        map.set(
          s.trackId,
          profileSkillsTrackDisplayTitle(s.trackTitle || s.trackId, s.language, languageFilter)
        );
      }
    }
    return [...map.entries()].sort((a, b) =>
      a[1].localeCompare(b[1], undefined, { sensitivity: 'base' })
    );
  }, [accessibleSnippets, languageFilter]);

  const hasSnippetsWithoutTrack = useMemo(
    () => accessibleSnippets.some((s) => !s.trackId),
    [accessibleSnippets]
  );

  useEffect(() => {
    if (isPremiumUser || !skillsTrackFilter) return;
    if (skillsTrackFilter === USER_PROFILE_SKILLS_NO_TRACK_FILTER_VALUE) return;
    const stillValid = trackFilterOptions.some(([tid]) => tid === skillsTrackFilter);
    if (!stillValid) setSkillsTrackFilter('');
  }, [skillsTrackFilter, trackFilterOptions, isPremiumUser]);

  const tagFilterOptions = useMemo(() => {
    const byLower = new Map();
    for (const s of accessibleSnippets) {
      for (const t of s.snippetTags || []) {
        if (typeof t !== 'string' || !t.trim()) continue;
        const k = t.toLowerCase();
        if (!byLower.has(k)) byLower.set(k, t.trim());
      }
    }
    return [...byLower.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [accessibleSnippets]);

  const showSkillsTrackFilter = trackFilterOptions.length > 0 || hasSnippetsWithoutTrack;
  const showSkillsTagFilter = tagFilterOptions.length > 0;

  const skillsTrackMenuItems = useMemo(() => {
    const items = [{ value: '', label: 'All tracks' }];
    if (hasSnippetsWithoutTrack) {
      items.push({ value: USER_PROFILE_SKILLS_NO_TRACK_FILTER_VALUE, label: 'No track' });
    }
    for (const [tid, title] of trackFilterOptions) {
      items.push({ value: tid, label: title });
    }
    return items;
  }, [hasSnippetsWithoutTrack, trackFilterOptions]);

  const skillsTagMenuItems = useMemo(
    () => [{ value: '', label: 'All tags' }, ...tagFilterOptions.map((tag) => ({ value: tag, label: tag }))],
    [tagFilterOptions]
  );

  const skillsTrackFilterTriggerLabel = useMemo(() => {
    if (!skillsTrackFilter) return 'All tracks';
    if (skillsTrackFilter === USER_PROFILE_SKILLS_NO_TRACK_FILTER_VALUE) return 'No track';
    const hit = trackFilterOptions.find(([tid]) => tid === skillsTrackFilter);
    return hit ? hit[1] : skillsTrackFilter;
  }, [skillsTrackFilter, trackFilterOptions]);

  const skillsTagFilterTriggerLabel = useMemo(
    () => skillsTagFilter || 'All tags',
    [skillsTagFilter]
  );

  const snippetsAfterFilters = useMemo(() => {
    let list = accessibleSnippets;
    if (skillsTrackFilter === USER_PROFILE_SKILLS_NO_TRACK_FILTER_VALUE) {
      list = list.filter((s) => !s.trackId);
    } else if (skillsTrackFilter) {
      list = list.filter((s) => s.trackId === skillsTrackFilter);
    }
    if (skillsTagFilter) {
      const lo = skillsTagFilter.toLowerCase();
      list = list.filter((s) =>
        (s.snippetTags || []).some((t) => t.toLowerCase() === lo)
      );
    }
    if (
      skillsShowMasteredOnly ||
      skillsShowDueOnly ||
      skillsShowUnstartedOnly
    ) {
      list = list.filter((s) =>
        profileSkillsSnippetMatchesStatusFilters(s, {
          showMastered: skillsShowMasteredOnly,
          showDue: skillsShowDueOnly,
          showUnstarted: skillsShowUnstartedOnly
        })
      );
    }
    const q = skillsSnippetSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const title = (s.snippetTitle || '').toLowerCase();
        const deck = (s.deckName || '').toLowerCase();
        const track = profileSkillsTrackDisplayTitle(
          s.trackTitle || s.trackId,
          s.language,
          languageFilter
        ).toLowerCase();
        const tags = (s.snippetTags || []).join(' ').toLowerCase();
        return title.includes(q) || deck.includes(q) || track.includes(q) || tags.includes(q);
      });
    }
    return list;
  }, [
    accessibleSnippets,
    skillsTrackFilter,
    skillsTagFilter,
    skillsSnippetSearch,
    skillsShowMasteredOnly,
    skillsShowDueOnly,
    skillsShowUnstartedOnly,
    languageFilter
  ]);

  const orderedSnippets = snippetsAfterFilters;

  const { paginatedItems, totalPages, rangeStart, rangeEnd } = useMemo(() => {
    const total = orderedSnippets.length;
    const tp = Math.max(1, Math.ceil(total / pageSize));
    const start = (currentPage - 1) * pageSize;
    const slice = orderedSnippets.slice(start, start + pageSize);
    const rs = total === 0 ? 0 : start + 1;
    const re = Math.min(start + pageSize, total);
    return { paginatedItems: slice, totalPages: tp, rangeStart: rs, rangeEnd: re };
  }, [orderedSnippets, currentPage, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(orderedSnippets.length / pageSize));
    setCurrentPage((p) => Math.min(Math.max(1, p), maxPage));
  }, [orderedSnippets.length, pageSize]);

  const handlePageSizeChange = (e) => {
    const n = parseInt(e.target.value, 10);
    if (!USER_PROFILE_SKILLS_PAGE_SIZE_OPTIONS.includes(n)) return;
    setPageSize(n);
    setCurrentPage(1);
    try {
      sessionStorage.setItem(USER_PROFILE_SKILLS_PAGE_SIZE_STORAGE_KEY, String(n));
    } catch {
      /* ignore */
    }
  };

  const hasActiveStatusFilter =
    skillsShowMasteredOnly || skillsShowDueOnly || skillsShowUnstartedOnly;

  const statusFilterEmptyMessage = useMemo(() => {
    const active = [];
    if (skillsShowMasteredOnly) active.push('mastered');
    if (skillsShowDueOnly) active.push('due');
    if (skillsShowUnstartedOnly) active.push('unstarted');
    if (active.length === 1) {
      return `No ${active[0]} snippets match the current filters.`;
    }
    return 'No snippets match the selected status filters.';
  }, [skillsShowMasteredOnly, skillsShowDueOnly, skillsShowUnstartedOnly]);

  if (!canView) {
    return (
      <p className="user-profile-achievement-stats-panel__private">
        Snippets are only visible on your own profile.
      </p>
    );
  }

  const totalSnippets = accessibleSnippets.length;

  return (
    <div
      className={`user-profile-achievement-stats-panel${isRefreshingSkills ? ' user-profile-achievement-stats-panel--refreshing' : ''}`}
      role="region"
      aria-label="Snippets"
      aria-busy={isFetching}
    >
      <div className="user-profile-achievement-stats-panel__breakdown">
        {showInitialSkeleton ? (
          <>
            <UserProfileSkillsFiltersSkeleton />
            <UserProfileSkillsSnippetSkeleton />
          </>
        ) : totalSnippets === 0 ? (
          <p className="user-profile-achievement-stats-panel__breakdown-empty">
            No cookbook snippets in this language scope yet.
          </p>
        ) : (
          <>
            <div
              className="user-profile-achievement-stats-panel__skills-filters"
              role="toolbar"
              aria-label="Filter and search snippets"
            >
              <div className="user-profile-achievement-stats-panel__skills-filters-main">
                {showSkillsTrackFilter ? (
                  <div
                    className="user-profile-achievement-stats-panel__skills-filter-wrap"
                    ref={skillsTrackFilterWrapRef}
                  >
                    <button
                      type="button"
                      id="user-profile-achievement-stats-panel-track-filter-trigger"
                      className={`user-profile-achievement-stats-panel__skills-filter-trigger ${skillsFilterMenuOpen === 'track' ? 'user-profile-achievement-stats-panel__skills-filter-trigger--open' : ''}`}
                      aria-label={`Track filter: ${skillsTrackFilterTriggerLabel}`}
                      aria-expanded={skillsFilterMenuOpen === 'track'}
                      aria-haspopup="listbox"
                      onClick={() =>
                        setSkillsFilterMenuOpen((o) => (o === 'track' ? null : 'track'))
                      }
                    >
                      <RoadmapNodeIcon
                        className="user-profile__achievements-stat-pill__icon user-profile__achievements-stat-pill__icon--roadmap"
                        aria-hidden
                      />
                      <span className="user-profile-achievement-stats-panel__skills-filter-trigger-value">
                        {skillsTrackFilterTriggerLabel}
                      </span>
                      <span
                        className="user-profile-achievement-stats-panel__skills-filter-trigger-arrow"
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {skillsFilterMenuOpen === 'track' ? (
                      <div
                        className="user-profile-achievement-stats-panel__skills-filter-dropdown"
                        role="listbox"
                        aria-labelledby="user-profile-achievement-stats-panel-track-filter-trigger"
                      >
                        {skillsTrackMenuItems.map((item) => {
                          const selected = skillsTrackFilter === item.value;
                          const optKey =
                            item.value === ''
                              ? '__all_tracks__'
                              : item.value === USER_PROFILE_SKILLS_NO_TRACK_FILTER_VALUE
                                ? USER_PROFILE_SKILLS_NO_TRACK_FILTER_VALUE
                                : item.value;
                          return (
                            <button
                              key={optKey}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              className={`user-profile-achievement-stats-panel__skills-filter-option ${selected ? 'user-profile-achievement-stats-panel__skills-filter-option--selected' : ''}`}
                              onClick={() => {
                                setSkillsTrackFilter(item.value);
                                setSkillsTagFilter('');
                                setSkillsFilterMenuOpen(null);
                              }}
                            >
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {showSkillsTagFilter ? (
                  <div
                    className={`user-profile-achievement-stats-panel__skills-filter-wrap ${showSkillsTrackFilter ? 'user-profile-achievement-stats-panel__skills-filter-wrap--separated' : ''}`}
                    ref={skillsTagFilterWrapRef}
                  >
                    <button
                      type="button"
                      id="user-profile-achievement-stats-panel-tag-filter-trigger"
                      className={`user-profile-achievement-stats-panel__skills-filter-trigger ${skillsFilterMenuOpen === 'tag' ? 'user-profile-achievement-stats-panel__skills-filter-trigger--open' : ''}`}
                      aria-label={`Tag filter: ${skillsTagFilterTriggerLabel}`}
                      aria-expanded={skillsFilterMenuOpen === 'tag'}
                      aria-haspopup="listbox"
                      onClick={() => setSkillsFilterMenuOpen((o) => (o === 'tag' ? null : 'tag'))}
                    >
                      <Tag
                        className="user-profile-achievement-stats-panel__skills-filter-trigger-icon user-profile-achievement-stats-panel__skills-filter-trigger-icon--tag"
                        aria-hidden
                        strokeWidth={2}
                      />
                      <span className="user-profile-achievement-stats-panel__skills-filter-trigger-value">
                        {skillsTagFilterTriggerLabel}
                      </span>
                      <span
                        className="user-profile-achievement-stats-panel__skills-filter-trigger-arrow"
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {skillsFilterMenuOpen === 'tag' ? (
                      <div
                        className="user-profile-achievement-stats-panel__skills-filter-dropdown"
                        role="listbox"
                        aria-labelledby="user-profile-achievement-stats-panel-tag-filter-trigger"
                      >
                        {skillsTagMenuItems.map((item) => {
                          const selected = skillsTagFilter === item.value;
                          const optKey = item.value === '' ? '__all_tags__' : `tag:${item.value}`;
                          return (
                            <button
                              key={optKey}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              className={`user-profile-achievement-stats-panel__skills-filter-option ${selected ? 'user-profile-achievement-stats-panel__skills-filter-option--selected' : ''}`}
                              onClick={() => {
                                setSkillsTagFilter(item.value);
                                setSkillsTrackFilter('');
                                setSkillsFilterMenuOpen(null);
                              }}
                            >
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="user-profile-achievement-stats-panel__skills-status-toggles">
                  <UserProfileSkillsStatusToggle
                    checked={skillsShowDueOnly}
                    onChange={(e) => setSkillsShowDueOnly(e.target.checked)}
                    showLabel="Show due"
                    hideLabel="Hide due"
                    ariaLabelOff="Show due snippets only"
                    ariaLabelOn="Showing due snippets only. Uncheck to show all snippets."
                    separated={showSkillsTrackFilter || showSkillsTagFilter}
                    variant="due"
                    icon={
                      <Clock
                        className="user-profile-achievement-stats-panel__skills-status-toggle-icon"
                        aria-hidden
                        strokeWidth={2}
                      />
                    }
                  />
                  <UserProfileSkillsStatusToggle
                    checked={skillsShowUnstartedOnly}
                    onChange={(e) => setSkillsShowUnstartedOnly(e.target.checked)}
                    showLabel="Show unstarted"
                    hideLabel="Hide unstarted"
                    ariaLabelOff="Show unstarted snippets only"
                    ariaLabelOn="Showing unstarted snippets only. Uncheck to show all snippets."
                    variant="unstarted"
                    icon={
                      <CircleDashed
                        className="user-profile-achievement-stats-panel__skills-status-toggle-icon"
                        aria-hidden
                        strokeWidth={2}
                      />
                    }
                  />
                  <UserProfileSkillsStatusToggle
                    checked={skillsShowMasteredOnly}
                    onChange={(e) => setSkillsShowMasteredOnly(e.target.checked)}
                    showLabel="Show mastered"
                    hideLabel="Hide mastered"
                    ariaLabelOff="Show mastered snippets only"
                    ariaLabelOn="Showing mastered snippets only. Uncheck to show all snippets."
                    variant="mastered"
                    icon={
                      <MasteryBrainIcon className="user-profile-achievement-stats-panel__skills-status-toggle-icon user-profile-achievement-stats-panel__skills-status-toggle-icon--mastery" />
                    }
                  />
                </div>
              </div>
              <div
                className={`user-profile-achievement-stats-panel__skills-search-wrap ${skillsSearchOpen || skillsSnippetSearch ? 'user-profile-achievement-stats-panel__skills-search-wrap--open' : ''}`}
              >
                <div
                  className="user-profile-achievement-stats-panel__skills-search-inner"
                  role="search"
                  tabIndex={skillsSearchOpen ? -1 : 0}
                  onClick={() => {
                    if (!skillsSearchOpen) {
                      setSkillsSearchOpen(true);
                      window.setTimeout(() => skillsSearchInputRef.current?.focus(), 220);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if (!skillsSearchOpen) {
                        e.preventDefault();
                        setSkillsSearchOpen(true);
                        window.setTimeout(() => skillsSearchInputRef.current?.focus(), 220);
                      }
                    }
                  }}
                >
                  <UserAchievementStatsPanelSearchIcon
                    className="user-profile-achievement-stats-panel__skills-search-icon"
                    aria-hidden
                  />
                  <input
                    ref={skillsSearchInputRef}
                    id="user-profile-achievement-stats-panel-snippet-search"
                    type="search"
                    className="user-profile-achievement-stats-panel__skills-search-input"
                    placeholder="Search snippets…"
                    value={skillsSnippetSearch}
                    onChange={(e) => setSkillsSnippetSearch(e.target.value)}
                    onFocus={() => setSkillsSearchOpen(true)}
                    onBlur={() => {
                      if (!skillsSnippetSearch.trim()) {
                        setSkillsSearchOpen(false);
                      }
                    }}
                    aria-label="Search snippets by title, cookbook, track, or tag"
                  />
                </div>
              </div>
            </div>
            {snippetsAfterFilters.length === 0 ? (
              <p className="user-profile-achievement-stats-panel__breakdown-empty">
                {skillsSnippetSearch.trim()
                  ? `No snippets match "${skillsSnippetSearch.trim()}". Try another term or clear the search.`
                  : hasActiveStatusFilter
                    ? statusFilterEmptyMessage
                    : 'No snippets match the selected filter.'}
              </p>
            ) : (
              <>
                <ul className="user-profile-achievement-stats-panel__snippet-list">
                  {paginatedItems.map((row) => {
                    const canOpenSnippet =
                      typeof onStartStudying === 'function' && row.deckId && row.cardId;
                    const openSnippet = () => {
                      if (!canOpenSnippet) return;
                      if (
                        row.trackId &&
                        !profileSkillsSnippetIsAccessible(row, isPremiumUser)
                      ) {
                        window.dispatchEvent(new CustomEvent('navigateToSubscription'));
                        return;
                      }
                      onStartStudying({
                        deckId: row.deckId,
                        startCardId: row.cardId,
                        studyReturnContext: {
                          achievementsPanelView: 'skills',
                          achievementsLanguageFilter: languageFilter || ''
                        }
                      });
                    };
                    return (
                      <li
                        key={`${row.deckId}_${row.cardId}`}
                        className="user-profile-achievement-stats-panel__snippet-row-item"
                      >
                        <button
                          type="button"
                          className="user-profile-achievement-stats-panel__snippet-row"
                          onClick={openSnippet}
                          disabled={!canOpenSnippet}
                          aria-label={`Open snippet in study: ${row.snippetTitle}`}
                        >
                          <div className="user-profile-achievement-stats-panel__snippet-body">
                            <div className="user-profile-achievement-stats-panel__snippet-title-row">
                              <IconRenderer
                                name={languageIconNameFromKey(row.language)}
                                type="language"
                                size="small"
                                className="user-profile-achievement-stats-panel__snippet-lang-icon"
                                aria-hidden
                              />
                              <span className="user-profile-achievement-stats-panel__snippet-title">
                                {row.snippetTitle}
                              </span>
                            </div>
                            <div className="user-profile-achievement-stats-panel__snippet-deck-row">
                              <div className="user-profile-achievement-stats-panel__snippet-deck-info">
                                <UserAchievementStatsPanelDeckIcon
                                  className="user-profile-achievement-stats-panel__snippet-deck-icon"
                                  aria-hidden
                                />
                                <span className="user-profile-achievement-stats-panel__snippet-deck-path">
                                  <span className="user-profile-achievement-stats-panel__snippet-deck-name">
                                    {row.deckName}
                                  </span>
                                  <span className="user-profile-achievement-stats-panel__snippet-deck-lang">
                                    {languageDisplayLabel(row.language)}
                                  </span>
                                </span>
                              </div>
                              <div
                                className="user-profile-achievement-stats-panel__snippet-srs-status"
                                aria-label={`Review status: ${row.reviewLabel || 'Not started'}`}
                              >
                                <span
                                  className={`user-profile-achievement-stats-panel__snippet-srs-status-pill user-profile-achievement-stats-panel__snippet-srs-status-pill--${cookbookSnippetSrsStatusPillVariant(row)}`}
                                >
                                  {row.reviewLabel || 'Not started'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <footer className="user-profile-achievement-stats-panel__footer">
                  <div className="user-profile-achievement-stats-panel__footer-inner">
                    <div className="user-profile-achievement-stats-panel__footer-size">
                      <label
                        htmlFor="user-profile-achievement-stats-panel-page-size"
                        className="user-profile-achievement-stats-panel__footer-label"
                      >
                        Per page
                      </label>
                      <select
                        id="user-profile-achievement-stats-panel-page-size"
                        className="user-profile-achievement-stats-panel__footer-select"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        aria-label="Snippets per page"
                      >
                        {USER_PROFILE_SKILLS_PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                    <nav
                      className="user-profile-achievement-stats-panel__footer-nav"
                      aria-label="Snippets list pages"
                    >
                      <button
                        type="button"
                        className="user-profile-achievement-stats-panel__footer-nav-btn"
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        aria-label="Previous page"
                      >
                        <ChevronLeft
                          className="user-profile-achievement-stats-panel__footer-nav-icon"
                          strokeWidth={2}
                        />
                      </button>
                      <span className="user-profile-achievement-stats-panel__footer-page-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        type="button"
                        className="user-profile-achievement-stats-panel__footer-nav-btn"
                        disabled={currentPage >= totalPages}
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        aria-label="Next page"
                      >
                        <ChevronRight
                          className="user-profile-achievement-stats-panel__footer-nav-icon"
                          strokeWidth={2}
                        />
                      </button>
                    </nav>
                    <p className="user-profile-achievement-stats-panel__footer-range" aria-live="polite">
                      Showing {rangeStart}–{rangeEnd} of {orderedSnippets.length}
                    </p>
                  </div>
                </footer>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
